import { useState, type Dispatch, type SetStateAction } from 'react'

import { Box, Button, Tag, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import {
  ApprovalsConfigurationDocument,
  ApprovalsGroupFragment,
  SetApprovalGroupAsDefaultDocument,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DeleteGroupModal from 'modules/approvals/components/ApprovalsConfigurationBlock/_ApprovalGroupDescription/DeleteGroupModal'
import EditGroupModal from 'modules/approvals/components/ApprovalsConfigurationBlock/_ApprovalGroupDescription/EditGroupModal'

interface Props {
  group: ApprovalsGroupFragment
  canEdit: boolean
  canDelete: boolean
}

interface IPopover {
  group: ApprovalsGroupFragment
  setDefault: () => void
  setEditGroup: Dispatch<SetStateAction<boolean>>
  setDeleteGroup: Dispatch<SetStateAction<boolean>>
  canEdit: boolean
  canDelete: boolean
}

const Popover = ({
  group,
  setDefault,
  setEditGroup,
  setDeleteGroup,
  canEdit,
  canDelete,
}: IPopover) => {
  if (!canEdit && !canDelete) return null

  return (
    <PopoverMenu type="list">
      <PopoverMenu.Trigger>
        <Button.Decorative type="secondary" iconName="ellipsis" />
      </PopoverMenu.Trigger>
      <PopoverMenu.Content side="bottom" align="center">
        {!group.default && canEdit && (
          <PopoverMenu.TouchableOption
            title={i18n.t('settings.approvals.approval_group.set_as_default')}
            onClick={setDefault}
          />
        )}
        {canEdit && (
          <PopoverMenu.TouchableOption
            title={i18n.t('settings.approvals.approval_group.modify')}
            onClick={() => setEditGroup(true)}
          />
        )}
        {!group.default && canDelete && (
          <PopoverMenu.TouchableOption
            title={i18n.t('settings.approvals.approval_group.delete.title')}
            danger
            onClick={() => setDeleteGroup(true)}
          />
        )}
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

const ApprovalGroupItem = ({ group, canEdit, canDelete }: Props) => {
  const { navigation } = useNavigation()

  const [editGroup, setEditGroup] = useState(false)
  const [tooltipOpen, toggleTooltip] = useToggle()
  const [tooltipOpenAutoapproval, toggleTooltipAutoapproval] = useToggle()
  const [setAsDefaultApprovalGroup, { loading: _loading }] = useMutation(
    SetApprovalGroupAsDefaultDocument,
    {
      extractorFn: (data) => data.approvalsMutations.setAsDefaultApprovalsApprovalGroup,
      refetchQueries: [ApprovalsConfigurationDocument],
      awaitRefetchQueries: true,
    }
  )

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const employeeGroupCount = group.groupMemberships?.length ?? 0

  const setDefault = async () => {
    const result = await setAsDefaultApprovalGroup({
      variables: {
        id: group.id,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('settings.approvals.approval_group.flash_messages.successful_update'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('settings.approvals.approval_group.flash_messages.error_update'),
        time: 2000,
      })
    }

    return handled
  }

  return (
    <>
      <Box flexDirection="row" width="full" justifyContent="spaceBetween">
        <Box minWidth="none" width="s240" gap="s2">
          <Box width="s240">
            <Text.Normal size="300" weight="medium" color="grey1000">
              {group.name}
            </Text.Normal>
          </Box>
          <Box gap="s8">
            <Text.Normal size="200" color="grey800">
              {i18n.t('settings.approvals.detail.subtitle', {
                number: employeeGroupCount,
              })}
            </Text.Normal>
            <Box flexDirection="row" gap="s8">
              {group.default && (
                <Tooltip.Dialog
                  open={tooltipOpen}
                  toggle={toggleTooltip}
                  attachment="bottom center"
                  label={i18n.t('settings.approvals.detail.default_tag_tooltip')}
                >
                  <Tag type={Tag.Types.viridian} solid>
                    {i18n.t('settings.approvals.detail.default_tag')}
                  </Tag>
                </Tooltip.Dialog>
              )}
              {group.autoApproved && (
                <Tooltip.Dialog
                  open={tooltipOpenAutoapproval}
                  toggle={toggleTooltipAutoapproval}
                  attachment="bottom center"
                  label={i18n.t('settings.approvals.detail.autoapproval_tag_tooltip')}
                >
                  <Tag type={Tag.Types.purple} solid>
                    {i18n.t('settings.approvals.detail.autoapproval_tag')}
                  </Tag>
                </Tooltip.Dialog>
              )}
            </Box>
          </Box>
        </Box>
        {canEdit && (
          <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
            <Popover
              group={group}
              setEditGroup={setEditGroup}
              setDeleteGroup={setDeleteModalOpen}
              setDefault={setDefault}
              canEdit={canEdit}
              canDelete={canDelete}
            />
            <Button.Router
              aria-label={i18n.t('settings.approvals.detail.button_description', {
                name: group.name,
              })}
              to={navigation.approvals.approvalGroupsCategoryId.fullPath({
                id: group.id,
                category: group.category,
              })}
              type="secondary"
              iconName="arrow-right"
            />
          </Box>
        )}
      </Box>
      {editGroup && <EditGroupModal onClose={() => setEditGroup(false)} group={group} />}
      {deleteModalOpen && (
        <DeleteGroupModal onClose={() => setDeleteModalOpen(false)} group={group} />
      )}
    </>
  )
}

export default ApprovalGroupItem
